import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Grid
} from '@mui/material';
import {
  Upload,
  FileText,
  CheckCircle,
  Download,
  Trash
} from '@phosphor-icons/react';
import api from '../../utils/api';

const documentTypes = [
  {
    key: 'certificateOfIncorporation',
    label: 'Certificate of Incorporation',
    description: 'For limited companies - shows your business is legally registered',
    required: false
  },
  {
    key: 'publicLiabilityInsurance',
    label: 'Public Liability Insurance',
    description: 'Essential for businesses dealing with the public - minimum £1M coverage',
    required: false
  },
  {
    key: 'businessInsurance',
    label: 'Business Insurance',
    description: 'Proof of business insurance coverage',
    required: false
  },
  {
    key: 'vatRegistration',
    label: 'VAT Registration Certificate',
    description: 'If you are VAT registered',
    required: false
  }
];

function DocumentUpload({ businessId, businessType, verificationDocuments, onUploadComplete, onBack, onNext }) {
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [localVerificationDocuments, setLocalVerificationDocuments] = useState(verificationDocuments);

  // Sync local state with props when verificationDocuments changes
  useEffect(() => {
    setLocalVerificationDocuments(verificationDocuments);
  }, [verificationDocuments]);

  const handleFileSelect = async (documentType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 
                         'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF, DOC, DOCX, JPEG, JPG, PNG, or GIF file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError('');
    setUploading(prev => ({ ...prev, [documentType]: true }));
    setUploadProgress(prev => ({ ...prev, [documentType]: 0 }));

    try {
      const formData = new FormData();
      formData.append(documentType, file);
      formData.append('businessId', businessId);

      const _response = await api.post(
        `/api/business-verification/${businessId}/upload-documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [documentType]: progress }));
          },
        }
      );

      // Call the completion callback to refresh the parent component
      if (onUploadComplete) {
        onUploadComplete();
      }

    } catch (error) {
      setError(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploading(prev => ({ ...prev, [documentType]: false }));
      setUploadProgress(prev => ({ ...prev, [documentType]: 0 }));
    }
  };

  const handleRemoveFile = async (documentType) => {
    // Get the document filename for the confirmation message
    const document = localVerificationDocuments[documentType];
    const filename = document?.filename || documentType;
    
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to remove "${filename}"?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) {
      return; // User cancelled the deletion
    }
    
    try {
      await api.delete(`/api/business-verification/${businessId}/remove-document/${documentType}`);
      
      // Update local state immediately to reflect the change
      setLocalVerificationDocuments(prev => {
        const updated = { ...prev };
        delete updated[documentType];
        return updated;
      });
      
      // Call the upload complete callback to refresh the parent component
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      setError('Failed to remove document. Please try again.');
    }
  };

  const getDocumentStatus = (documentType) => {
    const doc = localVerificationDocuments?.[documentType];
    // Check if document has actual content (not just an empty object)
    if (doc && doc.url && doc.filename && doc.uploadedAt) {
      return { status: 'uploaded', url: doc.url, filename: doc.filename };
    }
    return { status: 'missing' };
  };

  const getUploadedCount = () => {
    if (!localVerificationDocuments) return 0;
    return Object.keys(localVerificationDocuments).filter(key => {
      if (key === 'additionalDocuments') return false;
      const doc = localVerificationDocuments[key];
      // Only count documents that have actual content
      return doc && doc.url && doc.filename && doc.uploadedAt;
    }).length;
  };

  const minimumDocumentsRequired = businessType === 'sole_trader' ? 1 : 2;
  const uploadedCount = getUploadedCount();
  const remainingDocuments = Math.max(minimumDocumentsRequired - uploadedCount, 0);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <Upload size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
        Upload Verification Documents
      </Typography>
      
      <Typography variant="body2" color="text.secondary" mb={3}>
        Upload at least {minimumDocumentsRequired} document{minimumDocumentsRequired === 1 ? '' : 's'} from the list below to verify your business. All documents are stored securely and will be deleted after verification.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {documentTypes.map((docType) => {
          const docStatus = getDocumentStatus(docType.key);
          const isUploading = uploading[docType.key];
          const progress = uploadProgress[docType.key];

          return (
            <Grid item xs={12} sm={6} md={3} key={docType.key}>
              <Card 
                variant="outlined" 
                sx={{ 
                  height: '100%',
                  border: docStatus.status === 'uploaded' ? '2px solid #4caf50' : '1px solid #e0e0e0',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <FileText size={20} color="#666" />
                    {docStatus.status === 'uploaded' && (
                      <CheckCircle size={20} color="#4caf50" />
                    )}
                  </Box>

                  <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'center', fontSize: '0.875rem' }}>
                    {docType.label}
                  </Typography>

                  {docStatus.status === 'uploaded' ? (
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, textAlign: 'center', wordBreak: 'break-word' }}>
                        {docStatus.filename}
                      </Typography>
                      <Box display="flex" gap={1} mt="auto">
                        <Button
                          size="small"
                          component="a"
                          href={docStatus.url}
                          target="_blank"
                          download
                          startIcon={<Download size={14} />}
                          variant="outlined"
                          sx={{ flexGrow: 1, fontSize: '0.75rem', py: 0.5 }}
                        >
                          Download
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleRemoveFile(docType.key)}
                          variant="outlined"
                          color="error"
                          sx={{ minWidth: 'auto', px: 1, py: 0.5, '& .MuiButton-startIcon': { margin: 0 } }}
                        >
                          <Trash size={14} />
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <input
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                        style={{ display: 'none' }}
                        id={`upload-${docType.key}`}
                        type="file"
                        onChange={(e) => handleFileSelect(docType.key, e)}
                        disabled={isUploading}
                      />
                      <label htmlFor={`upload-${docType.key}`} style={{ flexGrow: 1, display: 'flex' }}>
                        <Button
                          variant="outlined"
                          component="span"
                          fullWidth
                          disabled={isUploading}
                          startIcon={<Upload size={14} />}
                          sx={{ mt: 'auto', fontSize: '0.75rem', py: 1 }}
                        >
                          {isUploading ? 'Uploading...' : 'Upload'}
                        </Button>
                      </label>
                      
                      {isUploading && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={progress} 
                            sx={{ mb: 0.5 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {progress}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Security:</strong> All documents are encrypted and stored securely. 
          They will be automatically deleted after verification is complete.
        </Typography>
      </Alert>

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
        <Box display="flex" alignItems="center" gap={2}>
          {onBack && (
            <Button
              onClick={onBack}
              size="large"
              sx={{ minWidth: 120 }}
            >
              Back
            </Button>
          )}
          <Typography variant="body2" color="text.secondary">
            Documents uploaded: {uploadedCount} of {documentTypes.length} (minimum {minimumDocumentsRequired} required)
          </Typography>
        </Box>
        
        <Box display="flex" gap={2}>
          {uploadedCount >= minimumDocumentsRequired && onNext ? (
            <Button
              variant="contained"
              onClick={onNext}
              size="large"
              sx={{ minWidth: 150 }}
            >
              Next Step
            </Button>
          ) : uploadedCount < minimumDocumentsRequired ? (
            <Typography variant="body2" color="text.secondary">
              Upload at least {remainingDocuments} more document{remainingDocuments === 1 ? '' : 's'} to continue
            </Typography>
          ) : null}
        </Box>
      </Box>

    </Box>
  );
}

export default DocumentUpload;

